/**
 * Turning balance auto-reload OFF — the confirmation flow, as pure functions.
 *
 * The parent's intent ("stop auto-reloading my wallet") is never in question.
 * What needs confirming is the CONSEQUENCE: auto-orders are charged to that
 * balance, so they cannot keep running without it. The server refuses the bare
 * disable with a 409 that NAMES those auto-orders
 * (`routes/billing/auto-reload.ts`); this module turns that refusal into a
 * confirmation and, on yes, re-sends the same request with the opt-in that
 * makes the server pause them and disable auto-reload in one transaction.
 *
 * Everything here is pure or dependency-injected so the two-step flow —
 * especially "cancel changes nothing" — is unit-testable without a browser.
 * The dialog does UI; this does the decision.
 *
 * Sibling module: `lib/auto-reload-alert.ts` owns the copy for the OTHER half
 * of the story (what a parent is told once auto-reload is already off).
 */

/** Server error code for "you still have balance-funded auto-orders". */
export const AUTO_RELOAD_LOCKED_BY_SUBSCRIPTIONS_CODE = 'auto_reload_required_by_subscriptions'

/**
 * One auto-order named on the 409, verbatim from the server's
 * `AffectedSubscriptionSummary`.
 */
export interface BlockedAutoOrder {
  id: string
  /** Parent-facing name — never a raw id. */
  label: string
  /** `Subscription.status`. `PAUSED` rows are reported but not re-paused. */
  status: string
}

/** Copy for the destructive-confirm step. */
export interface AutoReloadDisableConfirmCopy {
  title: string
  body: string
  confirmLabel: string
  cancelLabel: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * The affected auto-orders off a caught error, or null when this wasn't the
 * confirmable refusal.
 *
 * Reads structurally rather than `instanceof ApiError` so it can be tested
 * without the fetch layer, and tolerates a `subscriptions` array that predates
 * the `status` field (treated as not-paused, which is the safe reading — it
 * only ever moves a row into the "will be paused" sentence).
 */
export function parseAutoReloadBlockedSubscriptions(err: unknown): BlockedAutoOrder[] | null {
  if (!isRecord(err)) {
    return null
  }
  const body = isRecord(err['body']) ? err['body'] : null
  const code = err['code'] ?? body?.['error']
  if (code !== AUTO_RELOAD_LOCKED_BY_SUBSCRIPTIONS_CODE) {
    return null
  }
  const raw = body?.['subscriptions']
  if (!Array.isArray(raw)) {
    return []
  }
  const out: BlockedAutoOrder[] = []
  for (const entry of raw) {
    if (!isRecord(entry)) {
      continue
    }
    const id = entry['id']
    const label = entry['label']
    if (typeof id !== 'string' || typeof label !== 'string') {
      continue
    }
    out.push({ id, label, status: typeof entry['status'] === 'string' ? entry['status'] : 'ENABLED' })
  }
  return out
}

/** "a", "a and b", "a, b, and c" — matches the server's `formatLabelList`. */
export function formatAutoOrderList(labels: readonly string[]): string {
  if (labels.length === 0) {
    return ''
  }
  if (labels.length === 1) {
    return labels[0] ?? ''
  }
  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`
  }
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

/**
 * What the parent is asked before anything changes.
 *
 * Says the consequence FIRST (auto-orders get paused), names them, and states
 * that it is reversible — a parent who reads only the title still knows what
 * they are about to trade.
 *
 * An auto-order that is ALREADY paused is reported separately and never
 * promised as something this will pause: the server leaves those rows alone
 * (`partitionAffectedForCascade`), so claiming otherwise would be false.
 */
export function autoReloadDisableConfirmCopy(blocked: readonly BlockedAutoOrder[]): AutoReloadDisableConfirmCopy {
  const toPause = blocked.filter((sub) => sub.status !== 'PAUSED')
  const alreadyPaused = blocked.filter((sub) => sub.status === 'PAUSED')
  const cancelLabel = 'Keep auto-reload on'

  // Nothing to pause — every affected auto-order is already paused. Turning
  // auto-reload off is then only a statement about their future.
  if (toPause.length === 0) {
    const n = alreadyPaused.length
    const noun = n === 1 ? 'auto-order' : 'auto-orders'
    return {
      title: 'Turn off auto-reload?',
      body:
        `Your paused ${noun} ${formatAutoOrderList(alreadyPaused.map((s) => s.label))} ` +
        `${n === 1 ? 'is' : 'are'} charged to your household balance, so ${n === 1 ? 'it' : 'they'} ` +
        `can't be resumed until auto-reload is back on.`,
      confirmLabel: 'Turn off auto-reload',
      cancelLabel,
    }
  }

  const n = toPause.length
  const noun = n === 1 ? 'auto-order' : `${n} auto-orders`
  const object = n === 1 ? 'it' : 'them'
  const tail =
    alreadyPaused.length > 0
      ? ` ${formatAutoOrderList(alreadyPaused.map((s) => s.label))} ` +
        `${alreadyPaused.length === 1 ? 'is' : 'are'} already paused and will stay paused.`
      : ''
  return {
    title:
      n === 1
        ? 'Pause your auto-order to turn off auto-reload?'
        : `Pause your ${n} auto-orders to turn off auto-reload?`,
    body:
      `Auto-orders are charged to your household balance, so they can't run without auto-reload. ` +
      `Turning it off will pause your ${noun}: ${formatAutoOrderList(toPause.map((s) => s.label))}.${tail} ` +
      `You can turn auto-reload back on and resume ${object} anytime.`,
    confirmLabel: n === 1 ? 'Pause auto-order and turn off' : 'Pause auto-orders and turn off',
    cancelLabel,
  }
}

/**
 * The success toast. Reports what the SERVER actually paused, not what the
 * confirmation listed — the two differ when a row was cancelled between the
 * dialog opening and the parent confirming.
 */
export function autoReloadSaveToastMessage(args: { enabled: boolean; pausedCount: number }): string {
  if (args.enabled) {
    return 'Auto-reload enabled'
  }
  if (args.pausedCount === 1) {
    return 'Auto-reload off — 1 auto-order paused'
  }
  if (args.pausedCount > 1) {
    return `Auto-reload off — ${args.pausedCount} auto-orders paused`
  }
  return 'Auto-reload disabled'
}

export interface SaveAutoReloadVars {
  /** Household being edited — the PATCH 409s without it for a guardian on
   *  more than one household, rather than writing the oldest (PROD-1314). */
  accountId: string
  enabled: boolean
  thresholdCents: number | null
  reloadAmountCents: number | null
  pauseAffectedSubscriptions?: boolean
}

export type SaveAutoReloadOutcome =
  /** Saved on the first try — nothing depended on auto-reload. */
  | { status: 'SAVED'; pausedCount: 0 }
  /** Saved after the parent confirmed the cascade. */
  | { status: 'SAVED_WITH_CASCADE'; pausedCount: number }
  /** Parent declined at the confirmation. NOTHING was sent or changed. */
  | { status: 'CANCELLED' }

export interface SaveAutoReloadDeps {
  /** Issues the PATCH. Rejects with the server's error on non-2xx. */
  save: (vars: SaveAutoReloadVars) => Promise<{ pausedSubscriptionIds?: string[] }>
  /** Shows the destructive confirmation. Resolves false on cancel. */
  confirm: (copy: AutoReloadDisableConfirmCopy) => Promise<boolean>
}

/**
 * Save auto-reload settings, escalating to a confirmation only when the server
 * says the disable would strand auto-orders.
 *
 * Cancel is a TRUE no-op: the second request is never issued, so the account
 * keeps auto-reload on and every subscription keeps its status. The caller must
 * likewise leave its draft toggle alone — re-flipping it on the parent's behalf
 * is the dead-end the 2026-08-03 build shipped and the user rejected.
 *
 * Any error that is NOT the confirmable refusal propagates untouched.
 */
export async function saveAutoReloadWithConfirm(
  vars: SaveAutoReloadVars,
  deps: SaveAutoReloadDeps,
): Promise<SaveAutoReloadOutcome> {
  try {
    await deps.save(vars)
    return { status: 'SAVED', pausedCount: 0 }
  } catch (err) {
    const blocked = parseAutoReloadBlockedSubscriptions(err)
    if (!blocked) {
      throw err
    }
    const confirmed = await deps.confirm(autoReloadDisableConfirmCopy(blocked))
    if (!confirmed) {
      return { status: 'CANCELLED' }
    }
    const result = await deps.save({ ...vars, pauseAffectedSubscriptions: true })
    return { status: 'SAVED_WITH_CASCADE', pausedCount: result.pausedSubscriptionIds?.length ?? 0 }
  }
}
